import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore'; // Import useAuthStore
import { useNavigate } from 'react-router-dom'; // Import useNavigate

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // const [role, setRole] = useState<'user' | 'admin'>('user'); // Role state removed
  const [error, setError] = useState('');
  const [isSignUpMode, setIsSignUpMode] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const setUser = useAuthStore((state) => state.setUser); // Get setUser from authStore
  const navigate = useNavigate(); // Initialize useNavigate


  const resetForm = () => { // keepRole parameter removed as role state is gone
    setEmail('');
    setPassword('');
    setError('');
    setSuccessMessage('');
  };

  const toggleMode = () => {
    setIsSignUpMode(!isSignUpMode);
    resetForm(); // Reset form when toggling mode
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (isSignUpMode) {
      try {
        // Signup always defaults to 'user' role
        const response = await axios.post('/api/signup', { email, password, role: 'user' });
        if (response.status === 201) {
          setSuccessMessage('Signup successful! You can now log in.');
          setIsSignUpMode(false); // Switch to login mode
          resetForm(); 
          // No automatic close, user can now login
        }
      } catch (err: any) {
        if (axios.isAxiosError(err) && err.response) {
          if (err.response.status === 409) {
            setError(err.response.data.error || 'User already exists. Please try logging in.');
          } else {
            setError(err.response.data.error || 'Signup failed. Please try again.');
          }
        } else {
          setError('An unexpected error occurred during signup.');
        }
      }
    } else { // Login mode
      try {
        // Login endpoint is now always /api/user/login
        const response = await axios.post('/api/user/login', { email, password });
        if (response.status === 200 && response.data.token) {
          localStorage.setItem('authToken', response.data.token);
          const userEmail = response.data.email;
          const userRole = response.data.role;
          setUser({ email: userEmail, role: userRole });
          
          resetForm(); // Reset form fields before navigation

          if (userRole === 'admin') {
            navigate('/admin/dashboard');
          } else {
            navigate('/dashboard'); // Default for 'user' role
          }
          onClose(); // Call onClose after navigation
        } else {
          // Handle cases where response is 200 but no token (should not happen with current server.js)
           setError('Login failed: Invalid server response.');
        }
      } catch (err: any) {
        if (axios.isAxiosError(err) && err.response) {
          setError(err.response.data.error || 'Login failed. Check credentials.');
        } else {
          setError('An unexpected error occurred during login.');
        }
      }
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/40"
            onClick={onClose}
          />
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="relative bg-white rounded-xl shadow-lg w-96 p-6" // Increased width for better layout
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold mb-4">
              {isSignUpMode ? 'Create Account' : 'Login'}
            </h2>
            {successMessage && (
              <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded-md">
                {successMessage}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="email"
                placeholder="Email"
                className="w-full border rounded px-3 py-2"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="Password"
                className="w-full border rounded px-3 py-2"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              {/* Role selector removed from UI */}
              {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
              <button
                type="submit"
                className="w-full bg-brand-600 text-white rounded px-3 py-2 hover:bg-brand-700 transition-colors"
              >
                {isSignUpMode ? 'Sign Up' : 'Sign In'}
              </button>
            </form>
            <button
              onClick={toggleMode}
              className="mt-4 text-sm text-brand-600 hover:underline w-full text-center"
            >
              {isSignUpMode
                ? 'Already have an account? Sign In'
                : "Don't have an account? Sign Up"}
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
